import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '../shared/TailwindComponents/Spinner';

interface CitationDocumentViewerProps {
  url: string;
  onClose: () => void;
}

const CitationDocumentViewer: React.FC<CitationDocumentViewerProps> = ({ url, onClose }) => {
  const { t } = useTranslation();
  const [isPdf, setIsPdf] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [canUseIframe, setCanUseIframe] = useState(false);

  useEffect(() => {
    setIsChecking(true);
    setCanUseIframe(false);
    setIsPdf(url.toLowerCase().endsWith('.pdf'));

    const checkFrameOptions = async () => {
      try {
        const response = await fetch(url, { method: 'HEAD' });
        const xFrameOptions = response.headers.get('X-Frame-Options');
        if (xFrameOptions && (xFrameOptions.toUpperCase() === 'SAMEORIGIN' || xFrameOptions.toUpperCase() === 'DENY')) {
          window.open(url, '_blank');
          onClose(); 
        } else {
          setCanUseIframe(true);
        }
      } catch (error) {
        if(!error) return
        window.open(url, '_blank');
        onClose(); 
      } finally {
        setIsChecking(false);
      }
    };

    checkFrameOptions();
  }, [url, onClose]);

  return (
    <div className="glass w-full h-full flex flex-col">
      <div className="flex justify-between items-center px-4 py-3 border-b border-hairline-soft">
        <h2 className="text-sm font-semibold text-fg-strong">{t('Document Viewer')}</h2>
        <button
          onClick={onClose}
          className="text-xs text-fg-muted hover:text-fg-strong transition-colors duration-200 ease-slide"
        >
          {t('Close')}
        </button>
      </div>

      <div className="flex-1 p-4 overflow-hidden">
        {isChecking ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Spinner size="sm" />
            <p className="text-xs text-fg-muted">{t('Checking document compatibility...')}</p>
          </div>
        ) : canUseIframe ? (
          isPdf ? (
            <div className="w-[93%] h-[88vh] mx-auto bg-surface-sunken border border-hairline-soft rounded-lg overflow-hidden">
              <iframe
                src={url}
                className="w-full h-full border-none"
                title={t('PDF Viewer')}
              />
            </div>
          ) : (
            <iframe
              src={url}
              className="w-full h-full border-none rounded-lg"
              title={t('Document Viewer')}
            />
          )
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-fg-muted">{t('Document opened in a new tab.')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitationDocumentViewer;